import { Injectable } from '@angular/core';
import {
    EntityCollectionServiceBase,
    EntityCollectionServiceElementsFactory,
} from 'ngrx-data';
import { Observable, of, NEVER, BehaviorSubject } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import { LoginService } from 'src/app/login/services/login-service.service';

import { Folder } from './folder.dto';
import { deepCopy } from '../general/generic.function';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';

@Injectable({
    providedIn: 'root',
})
export class FolderService extends EntityCollectionServiceBase<Folder> {
    private currentEntities: Folder[];
    constructor(
        serviceElementsFactory: EntityCollectionServiceElementsFactory,
        private loginServce: LoginService,
        private snackBar: MatSnackBar
    ) {
        super('Folder', serviceElementsFactory);


        this.entities$.subscribe(v => {
            this.currentEntities = v;
        });

        loginServce.loggedOut$.subscribe(v => {
            this.clearCache();
        });

    }


    displayByIds(folderIds: string[]) {
        // console.log('displayByIds', folderIds);
        return this.entities$.pipe(
            map(v => v.filter(ent => folderIds.indexOf(ent.id) >= 0))
        );
    }

    displayById(folderId: string) {
        // console.log('displayById');
        return this.entities$.pipe(
            map(v => v.find(ent => ent.id === folderId))
        );
    }

    // displayRootFolder() {
    //     console.log('displayRootFolder');
    //     return of(this.currentEntities.find(v => v.isRoot));
    // }

    // tslint:disable-next-line: member-ordering
    getIds$ = {};

    getByIds(
        objectStoreValue: string,
        folderIds: string[]
    ): Observable<Folder[]> {
        // console.log('Get By Ids ', folderIds);
        const entityKeys = this.currentEntities.map(v => v.id);
        const idsNotInCache = folderIds.filter(v => entityKeys.indexOf(v) < 0);
        const idsInCache = folderIds.filter(v => entityKeys.indexOf(v) >= 0);

        // console.log('L', this.currentEntities.length, idsNotInCache.length);
        if (idsNotInCache.length > 0) {
            // console.log('Not in cache', folderIds);
            const key = idsNotInCache.reduce(
                (prev, curr) => `${prev}${curr}`,
                ''
            );
            if (this.getIds$[key] === null || this.getIds$[key] === undefined) {
                this.getIds$[key] = super
                    .getWithQuery({
                        objectStore: objectStoreValue,
                        folderIds: idsNotInCache,
                    })
                    .pipe(
                        map(v => [
                            ...v,
                            ...idsInCache.map(row => this.currentEntities[row]),
                        ])
                    );
            }
            return this.getIds$[key];
        } else {
            //console.log('Returing in cache');
            return of(
                idsInCache.map(row =>
                    this.currentEntities.find(ent => ent.id === row)
                )
            );
        }
    }

    getByIdsForManageUser(
        objectStoreValue: string,
        folderIds: string[],
        complete: boolean = false
    ): Observable<Folder[]> {

        return super.getWithQuery({
            objectStore: objectStoreValue,
            folderIds: folderIds,
            complete: complete ? 'true' : 'false'

        })
        const entityKeys = this.currentEntities
            .filter(v => !v.isRoot && v.complete)
            .map(v => v.id);
        const completeIdsNotInCache = folderIds.filter(v => !entityKeys.includes(v));
        const completeIdsInCache = folderIds.filter(v => entityKeys.includes(v));
        if (completeIdsNotInCache.length > 0) {
            const key = completeIdsNotInCache.reduce(
                (prev, curr) => `${prev}${curr}`,
                ''
            );

            if (this.getIds$[key] === null || this.getIds$[key] === undefined) {
                this.getIds$[key] = super
                    .getWithQuery({
                        objectStore: objectStoreValue,
                        folderIds: completeIdsNotInCache,
                        complete: complete ? 'true' : 'false'

                    })
                    .pipe(
                        map(v => [
                            ...v,
                            ...completeIdsInCache.map(id => this.currentEntities.find(entity => entity.id === id)),
                        ]),
                        map(v => v.sort((a, b) => a.propertiesObj.FolderName.localeCompare(b.propertiesObj.FolderName))) // Sort the fetched data by FolderName property

                    );
            }
            return this.getIds$[key];
        } else {
            //console.log('Returing in cache');
            return of(
                completeIdsInCache.map(row =>
                    this.currentEntities.find(ent => ent.id === row)
                ).sort((a, b) => a.propertiesObj.FolderName.localeCompare(b.propertiesObj.FolderName)) // Sort the cached data by FolderName property
            );
        }
    }

    getRootFolder(objectStoreValue: string): Observable<Folder> {
        // console.log('Get Root Folder');
        return this.getByID(objectStoreValue, null);
    }

    getRoot(objectStoreValue: string, complete = false): Observable<Folder[]> {
        // console.log('Get Root Folder');
        return this.getByID(objectStoreValue, null).pipe(
            switchMap(v => this.getByIds(objectStoreValue, v.subFolders))
        );
    }

    getRootForUserManagement(objectStoreValue: string, complete: boolean): Observable<Folder[]> {
        return this.getByID(objectStoreValue, null, null, complete).pipe(
            switchMap(v => {
                return this.getByIdsForManageUser(objectStoreValue, v.subFolders)
            })
        );
    }

    loadRoot$ = {};
    loadID$ = {};
    completeIDs = [];

    updateFolderInCache(folderID, newDocId) {
        const tf = deepCopy(
            this.currentEntities.find(v => v.id === folderID)
        ) as Folder;
        tf.documents.push(newDocId);

        super.updateOneInCache(tf);
    }




    getByID(
        objectStoreValue: string,
        idValue: string,
        reload = false,
        complete = false
    ): Observable<Folder> {
        // console.log('GETTING', idValue, complete);
        if (objectStoreValue === 'NO ACCESS') {
            const snak = this.snackBar.open(
                'You have no access, logging out',
                'OK',
                {
                    duration: 10000,
                }
            );
            snak.afterDismissed().subscribe(_ => {
                this.loginServce.logOut();
            });

            return NEVER;
        }
        if (idValue === undefined || idValue === null) {
            let foundRoot = this.currentEntities.find(v => v.isRoot);
            if (foundRoot && foundRoot.objectStore !== objectStoreValue) {
                console.log('NEED TO RELOAD FOLDERS');
                this.clearCache();
                foundRoot = undefined;
            }
            // console.log('Loading Root', foundRoot, objectStoreValue);
            if (!reload && !!foundRoot && (!complete || foundRoot.complete)) {
                // console.log('Root in cache', objectStoreValue);
                return of(foundRoot);
            } else {
                // console.log('Root not in cache', this.loadRoot$);

                if (this.loadRoot$[objectStoreValue] == null || this.loadRoot$[objectStoreValue] == undefined) {
                    this.loadRoot$[objectStoreValue] = super
                        .getWithQuery({
                            objectStore: objectStoreValue,
                            folderIds: [],
                            complete: complete ? 'true' : 'false',
                        })
                        .pipe(map(v => v[0]));
                }
                return this.loadRoot$[objectStoreValue];
            }
        }

        const findEntity = this.currentEntities.find(v => v.id === idValue);
        // console.log(
        //     idValue,
        //     findEntity,
        //     !reload && !!findEntity && (!complete || findEntity.complete)
        // );

        if (!reload && !!findEntity && (!complete || findEntity.complete)) {
            // console.log('In cache ID', idValue);
            return of(findEntity);
        } else {
            // console.log('Not in cache ID', idValue);
            if (this.loadID$[idValue] == null) {
                // console.log('Calling backend', complete);
                if (complete && this.completeIDs.indexOf(idValue) < 0) {
                    this.completeIDs.push(idValue);
                }
                this.loadID$[idValue] = super
                    .getWithQuery({
                        objectStore: objectStoreValue,
                        folderIds: [idValue],
                        complete: complete ? 'true' : 'false',
                    })
                    .pipe(map(v => v[0]));
            }

            if (complete && this.completeIDs.indexOf(idValue) < 0) {
                // Need to get full one so partial one needs to be overridden
                this.loadID$[idValue] = super
                    .getWithQuery({
                        objectStore: objectStoreValue,
                        folderIds: [idValue],
                        complete: complete ? 'true' : 'false',
                    })
                    .pipe(map(v => v[0]));
            }

            return this.loadID$[idValue];
        }
    }

    updateFolders(folders: Folder[]) {

    }
}
